import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Filter,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useToast } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { RootState } from '../store';
import { DateInput } from '../components/DateInput';

type ApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type Discipline = 'TATTOO' | 'PIERCING';
type SortKey = 'createdAt' | 'reviewedAt' | 'lastName' | 'status';

const LIMIT = 20;

interface PractitionerApplication {
  id: string;
  status: ApplicationStatus;
  firstName: string;
  lastName: string;
  artistName?: string | null;
  email: string;
  phone: string;
  disciplines: Discipline[];
  message?: string | null;
  portfolioUrl?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
  reviewComment?: string | null;
  reviewedByAdmin?: { id: string; name: string; email: string } | null;
  approvedPractitioner?: { id: string; firstName: string; lastName: string; email: string } | null;
}

const statusBadge = (s: ApplicationStatus) => {
  if (s === 'PENDING')
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock className="w-3 h-3 mr-1" />En attente
      </span>
    );
  if (s === 'APPROVED')
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Check className="w-3 h-3 mr-1" />Approuvée
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <X className="w-3 h-3 mr-1" />Rejetée
    </span>
  );
};

const formatDate = (v?: string | null) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function PractitionerApplicationsPage() {
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<PractitionerApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | ''>('PENDING');
  const [disciplineFilter, setDisciplineFilter] = useState<Discipline | ''>('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Action modal (approve / reject single)
  const [actionModal, setActionModal] = useState<{
    isOpen: boolean;
    mode: 'approve' | 'reject';
    id: string | null;
  }>({ isOpen: false, mode: 'approve', id: null });
  const [reviewReason, setReviewReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Soft-delete single confirm
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; id: string | null }>({
    isOpen: false,
    id: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk definitive delete (hard)
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);

  const isBusy = loading || actionLoading || deleteLoading || bulkHardDeleting;

  const visibleIds = useMemo(() => items.map((i) => i.id), [items]);
  const {
    selectedIds,
    selectedCount,
    selectedArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
        sortBy: sortKey,
        sortDir,
        ...(statusFilter && { status: statusFilter }),
        ...(disciplineFilter && { discipline: disciplineFilter }),
        ...(search && { search }),
        ...(fromDate && { from: fromDate }),
        ...(toDate && { to: toDate }),
      });

      const resp = await apiClient(`/api/practitioner-applications/admin?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok || !data?.success) throw new Error(data?.message || 'Impossible de charger les demandes');

      setItems(data.data.applications || []);
      setTotal(data.data.total || 0);
      setTotalPages(data.data.totalPages || 1);
      clearSelection();
    } catch (e: any) {
      setError(e?.message || 'Impossible de charger les demandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, disciplineFilter, search, fromDate, toDate, sortKey, sortDir, page]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('PENDING');
    setDisciplineFilter('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  // ── Approve / Reject ────────────────────────────────────────────────────────
  const openActionModal = (mode: 'approve' | 'reject', id: string) => {
    setReviewReason('');
    setActionModal({ isOpen: true, mode, id });
  };
  const closeActionModal = () => setActionModal({ isOpen: false, mode: 'approve', id: null });

  const confirmAction = async () => {
    if (!actionModal.id) return;
    try {
      setActionLoading(true);
      const url = `/api/practitioner-applications/admin/${actionModal.id}/${actionModal.mode === 'approve' ? 'approve' : 'reject'}`;
      const body = actionModal.mode === 'reject' ? JSON.stringify({ reason: reviewReason.trim() || undefined }) : undefined;
      const resp = await apiClient(url, { method: 'POST', ...(body ? { body } : {}) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) throw new Error(data?.message || 'Échec du traitement');
      showToast(actionModal.mode === 'approve' ? 'Demande approuvée avec succès' : 'Demande rejetée', 'success');
      closeActionModal();
      await fetchApplications();
    } catch (e: any) {
      showToast(e?.message || 'Échec du traitement', 'error', 7000);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Single delete (soft) ────────────────────────────────────────────────────
  const confirmSingleDelete = async () => {
    if (!deleteConfirm.id) return;
    try {
      setDeleteLoading(true);
      const resp = await apiClient(`/api/practitioner-applications/admin/${deleteConfirm.id}`, { method: 'DELETE' });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) throw new Error(data?.message || 'Suppression impossible');
      showToast('Demande supprimée', 'success');
      setDeleteConfirm({ isOpen: false, id: null });
      await fetchApplications();
    } catch (e: any) {
      showToast(e?.message || 'Suppression impossible', 'error', 7000);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Bulk hard delete ─────────────────────────────────────────────────────────
  const openBulkHardDelete = () => {
    if (!canHardDelete || selectedArray.length === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, ids: selectedArray });
  };

  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.ids.length === 0) return;
    setBulkHardDeleting(true);
    const failed: string[] = [];
    try {
      for (const id of bulkHardDeleteConfirm.ids) {
        try {
          const resp = await apiClient(`/api/practitioner-applications/admin/${id}?hardDelete=true`, { method: 'DELETE' });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || !data?.success) failed.push(id);
        } catch {
          failed.push(id);
        }
      }
      const deletedCount = bulkHardDeleteConfirm.ids.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} demande(s) supprimée(s) définitivement`, 'success');
      } else if (deletedCount === 0) {
        showToast(`Aucune demande supprimée. ${failed.length} échec(s).`, 'error', 7000);
      } else {
        showToast(`${deletedCount} supprimée(s) définitivement. ${failed.length} échec(s).`, 'warning', 7000);
      }
      setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
      await fetchApplications();
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const SortHeader = ({ label, colKey }: { label: string; colKey: SortKey }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(colKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortKey === colKey && (
          <ChevronDown className={`w-4 h-4 transform transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      <ToastComponent />

      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Demandes "Devenir partenaire"</h1>
            <p className="text-textSecondary mt-1">{total} demande{total !== 1 ? 's' : ''} au total</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => void fetchApplications()}
              className="btn-secondary flex items-center space-x-2"
              disabled={isBusy}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Rafraîchir</span>
            </button>

            {selectedCount > 0 && canHardDelete && (
              <button
                onClick={openBulkHardDelete}
                disabled={isBusy}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                title="Supprimer définitivement les demandes sélectionnées"
              >
                <AlertCircle className="w-4 h-4" />
                <span>Suppression définitive ({selectedCount})</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="relative md:col-span-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Rechercher (nom / email / tél.)…"
              className="input-field pl-10"
              disabled={isBusy}
            />
          </div>

          <div className="relative md:col-span-2">
            <select
              className="input-field appearance-none pr-10"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
              disabled={isBusy}
            >
              <option value="PENDING">En attente</option>
              <option value="APPROVED">Approuvées</option>
              <option value="REJECTED">Rejetées</option>
              <option value="">Tous les statuts</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-2">
            <select
              className="input-field appearance-none pr-10"
              value={disciplineFilter}
              onChange={(e) => { setDisciplineFilter(e.target.value as any); setPage(1); }}
              disabled={isBusy}
            >
              <option value="">Toutes les disciplines</option>
              <option value="TATTOO">Tatouage</option>
              <option value="PIERCING">Piercing</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de début</label>
            <DateInput
              value={fromDate}
              onChange={(value) => { setFromDate(value); setPage(1); }}
              className="input-field"
              disabled={isBusy}
            />
          </div>

          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de fin</label>
            <DateInput
              value={toDate}
              onChange={(value) => { setToDate(value); setPage(1); }}
              className="input-field"
              disabled={isBusy}
            />
          </div>

          <button
            onClick={resetFilters}
            className="btn-secondary flex items-center justify-center space-x-2 md:col-span-1"
            disabled={isBusy}
          >
            <Filter className="w-4 h-4" />
            <span className="md:hidden">Effacer</span>
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => void fetchApplications()} className="btn-primary flex items-center space-x-2" disabled={isBusy}>
            <span>Filtrer</span>
          </button>
          {selectedCount > 0 && (
            <button onClick={clearSelection} className="text-sm text-gray-600 hover:text-gray-900 underline" disabled={isBusy}>
              Vider la sélection
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button onClick={() => void fetchApplications()} className="mt-2 text-sm text-red-600 hover:text-red-700 underline">
            Réessayer
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  {canHardDelete && (
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={items.length === 0 || isBusy}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label="Sélectionner toutes les demandes"
                    />
                  )}
                </th>
                <SortHeader label="Créé le" colKey="createdAt" />
                <SortHeader label="Candidat" colKey="lastName" />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disciplines</th>
                <SortHeader label="Statut" colKey="status" />
                <SortHeader label="Traité le" colKey="reviewedAt" />
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Chargement…
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Clock className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Aucune demande trouvée</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {search || statusFilter || disciplineFilter || fromDate || toDate
                        ? 'Essayez de modifier vos filtres.'
                        : 'Aucune demande pour le moment.'}
                    </p>
                  </td>
                </tr>
              ) : (
                items.map((a) => (
                  <tr key={a.id} className="group hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {canHardDelete && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.id)}
                          disabled={isBusy}
                          onChange={() => toggleSelect(a.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          aria-label={`Sélectionner ${a.firstName} ${a.lastName}`}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(a.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {a.firstName} {a.lastName}
                        {a.artistName && <span className="ml-1 text-gray-500 font-normal">« {a.artistName} »</span>}
                      </div>
                      <div className="text-sm text-gray-500">{a.email}</div>
                      <div className="text-xs text-gray-400">{a.phone}</div>
                      {a.portfolioUrl && (
                        <a
                          href={a.portfolioUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline mt-0.5"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Portfolio
                        </a>
                      )}
                      {a.message && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{a.message}</p>
                      )}
                      {a.reviewComment && (
                        <p className="text-xs text-red-700 mt-1">Motif : {a.reviewComment}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(a.disciplines || []).map((d) => (
                          <span key={d} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${d === 'TATTOO' ? 'bg-purple-100 text-purple-800' : 'bg-pink-100 text-pink-800'}`}>
                            {d === 'TATTOO' ? 'Tatouage' : 'Piercing'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{statusBadge(a.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div>{formatDate(a.reviewedAt)}</div>
                      {a.reviewedByAdmin && (
                        <div className="text-xs text-gray-400">{a.reviewedByAdmin.name || a.reviewedByAdmin.email}</div>
                      )}
                      {a.approvedPractitioner && (
                        <div className="text-xs text-green-600">
                          → {a.approvedPractitioner.firstName} {a.approvedPractitioner.lastName}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end gap-2">
                        {a.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => openActionModal('approve', a.id)}
                              disabled={isBusy}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              title="Approuver la demande"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Approuver
                            </button>
                            <button
                              onClick={() => openActionModal('reject', a.id)}
                              disabled={isBusy}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                              title="Rejeter la demande"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Rejeter
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setDeleteConfirm({ isOpen: true, id: a.id })}
                          disabled={isBusy}
                          className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Supprimer la demande"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(page - 1) * LIMIT + 1}</span> à{' '}
                <span className="font-medium">{Math.min(page * LIMIT, total)}</span> sur{' '}
                <span className="font-medium">{total}</span> résultats
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || isBusy}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      disabled={isBusy}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pageNum
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || isBusy}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {loading && items.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement…</span>
          </div>
        </div>
      )}

      {/* Approve / Reject modal */}
      {actionModal.isOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${actionLoading ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-4 py-3 border-b">
              <h3 className="text-lg font-semibold">
                {actionModal.mode === 'approve' ? 'Approuver la demande' : 'Rejeter la demande'}
              </h3>
            </div>
            <div className="p-4 space-y-4 text-sm text-gray-700">
              {actionModal.mode === 'approve' ? (
                <p>Êtes-vous sûr de vouloir approuver cette demande et créer le compte praticien ?</p>
              ) : (
                <>
                  <p>Confirmez-vous le rejet de cette demande ?</p>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Motif de rejet (optionnel)</label>
                    <textarea
                      value={reviewReason}
                      onChange={(e) => setReviewReason(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                      rows={3}
                      placeholder="Ex : informations incomplètes"
                      disabled={actionLoading}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end space-x-2">
              <button onClick={closeActionModal} className="btn-secondary px-3 py-1" disabled={actionLoading}>
                Annuler
              </button>
              <button
                onClick={() => void confirmAction()}
                disabled={actionLoading}
                className={`px-3 py-1 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
                  actionModal.mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionLoading ? 'Traitement…' : actionModal.mode === 'approve' ? 'Approuver' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single delete confirm */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Supprimer la demande"
        message="Êtes-vous sûr de vouloir supprimer cette demande ?"
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmVariant="danger"
        isLoading={deleteLoading}
        onConfirm={() => void confirmSingleDelete()}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteConfirm({ isOpen: false, id: null });
        }}
      />

      {/* Bulk hard delete */}
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={() => void confirmBulkHardDelete()}
        onClose={() => {
          if (bulkHardDeleting) return;
          setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
        }}
        entityLabel="demande(s)"
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={bulkHardDeleting}
      />
    </div>
  );
}

export default PractitionerApplicationsPage;
